import { useMemo, useState } from 'react'
import {
  Badge,
  BadgeStatus,
  Button,
  Card,
  DataTable,
  FilterBar,
  Input,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatPersen, formatRupiah } from '@/lib/format'
import type { Anggaran, BarisAnggaran } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

/** Indikator peringatan serapan anggaran: 80% kuning, 90% jingga, 100% merah. */
export function IndikatorSerapan({ persen }: { persen: number }) {
  if (!Number.isFinite(persen)) return <span className="text-netral-400">—</span>
  const nada =
    persen >= 100 ? 'bahaya' : persen >= 90 ? 'bahaya' : persen >= 80 ? 'peringatan' : 'sukses'
  const label = persen >= 100 ? 'Terlampaui' : persen >= 90 ? 'Kritis' : persen >= 80 ? 'Peringatan' : 'Aman'
  return (
    <div className="flex flex-col items-end gap-1">
      <span className="angka-tabular font-medium text-netral-800">{formatPersen(persen)}</span>
      <Badge nada={nada}>{label}</Badge>
    </div>
  )
}

export default function AnggaranPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>(FILTER_KOSONG)
  const [statusFilter, setStatusFilter] = useState('')
  const [detail, setDetail] = useState<Anggaran | null>(null)
  const [revisi, setRevisi] = useState<Anggaran | null>(null)
  const aksi = useAksi()

  const daftar = usePaged<Anggaran>('/budgets', {
    query: {
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
    },
  })

  const kolom = useMemo<KolomTabel<Anggaran>[]>(
    () => [
      {
        kunci: 'budgetCode',
        judul: 'Kode',
        render: (b) => (
          <button
            type="button"
            className="font-medium text-yayasan-800 hover:underline"
            onClick={() => setDetail(b)}
          >
            {b.budgetCode}
          </button>
        ),
      },
      { kunci: 'name', judul: 'Nama Anggaran' },
      { kunci: 'unit', judul: 'Unit', render: (b) => b.unit?.name ?? '—' },
      { kunci: 'budgetType', judul: 'Jenis', render: (b) => b.budgetType },
      {
        kunci: 'totalFinal',
        judul: 'Anggaran Final',
        rata: 'kanan',
        render: (b) => formatRupiah(b.totalFinal || b.totalPlanned),
      },
      { kunci: 'totalRealized', judul: 'Realisasi', rata: 'kanan', render: (b) => formatRupiah(b.totalRealized) },
      {
        kunci: 'serapan',
        judul: 'Serapan',
        nilaiSort: (b) => (b.totalFinal || b.totalPlanned ? b.totalRealized / (b.totalFinal || b.totalPlanned) : 0),
        render: (b) => {
          const dasar = b.totalFinal || b.totalPlanned
          return <IndikatorSerapan persen={dasar ? (b.totalRealized / dasar) * 100 : NaN} />
        },
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
              Baris
            </Button>
            {punyaIzin('budget:submit') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => void jalankan(b.id, 'submit')}>
                Ajukan
              </Button>
            ) : null}
            {punyaIzin('budget:review') && b.status === 'SUBMITTED' ? (
              <Button ukuran="kecil" varian="garis" onClick={() => void jalankan(b.id, 'review')}>
                Tinjau
              </Button>
            ) : null}
            {punyaIzin('budget:approve') && b.status === 'REVIEWED' ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => void jalankan(b.id, 'approve')}>
                Setujui
              </Button>
            ) : null}
            {punyaIzin('budget:approve') && b.status === 'APPROVED' ? (
              <Button ukuran="kecil" onClick={() => void jalankan(b.id, 'activate')}>
                Aktifkan
              </Button>
            ) : null}
            {punyaIzin('budgetrevision:create') && (b.status === 'ACTIVE' || b.status === 'APPROVED') ? (
              <Button ukuran="kecil" varian="halus" onClick={() => setRevisi(b)}>
                Revisi
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [punyaIzin, aksi],
  )

  const jalankan = async (id: string, tindakan: string) => {
    const hasil = await aksi.jalankan(
      () => api.post(`/budgets/${id}/${tindakan}`, {}, { idempotencyKey: buatIdempotencyKey() }),
      { sukses: `Anggaran berhasil di-${tindakan}.` },
    )
    if (hasil !== null) daftar.muat()
  }

  return (
    <div>
      <PageHeader
        judul="Anggaran"
        deskripsi="Anggaran tahunan/bulanan per unit dengan pemantauan serapan dan peringatan 80/90/100%."
        ekspor={{
          path: '/reports/budget-realization',
          namaDasar: 'realisasi-anggaran',
          query: { unitId: filter.unitId || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: false, sampai: false, fundId: false, program: false, kategori: false }}
        ekstra={
          <Select
            label="Status"
            placeholder="Semua status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            opsi={['DRAFT', 'SUBMITTED', 'REVIEWED', 'APPROVED', 'ACTIVE', 'REJECTED', 'CLOSED'].map((s) => ({
              value: s,
              label: s,
            }))}
            className="min-w-40"
          />
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada anggaran."
      />

      <ModalBarisAnggaran anggaran={detail} onTutup={() => setDetail(null)} />
      <FormRevisi anggaran={revisi} onTutup={() => setRevisi(null)} setelah={daftar.muat} />
    </div>
  )
}

function ModalBarisAnggaran({ anggaran, onTutup }: { anggaran: Anggaran | null; onTutup: () => void }) {
  const { data, memuat, galat, kodeGalat, muat } = useApi<unknown>(
    anggaran ? `/budgets/${anggaran.id}/lines` : null,
    { perPage: 500 },
  )

  const baris: BarisAnggaran[] = Array.isArray(data)
    ? (data as BarisAnggaran[])
    : ((data as { items?: BarisAnggaran[] } | null)?.items ?? [])

  const kolom: KolomTabel<BarisAnggaran>[] = [
    {
      kunci: 'account',
      judul: 'Akun',
      render: (b) => (
        <div>
          <p className="font-medium text-netral-800">{b.account?.name ?? b.accountId}</p>
          <p className="text-xs text-netral-500">{b.account?.code ?? ''}</p>
        </div>
      ),
    },
    { kunci: 'periodNumber', judul: 'Periode', render: (b) => `Ke-${b.periodNumber}` },
    {
      kunci: 'anggaran',
      judul: 'Anggaran',
      rata: 'kanan',
      nilaiSort: (b) => b.finalAmount || b.revisedAmount || b.amount,
      render: (b) => formatRupiah(b.finalAmount || b.revisedAmount || b.amount),
    },
    { kunci: 'realizedAmount', judul: 'Realisasi', rata: 'kanan', render: (b) => formatRupiah(b.realizedAmount) },
    {
      kunci: 'sisa',
      judul: 'Sisa',
      rata: 'kanan',
      nilaiSort: (b) => (b.finalAmount || b.revisedAmount || b.amount) - b.realizedAmount,
      render: (b) => {
        const sisa = (b.finalAmount || b.revisedAmount || b.amount) - b.realizedAmount
        return <span className={sisa < 0 ? 'font-medium text-red-700' : ''}>{formatRupiah(sisa)}</span>
      },
    },
    {
      kunci: 'persentase',
      judul: 'Serapan',
      rata: 'kanan',
      render: (b) => {
        const dasar = b.finalAmount || b.revisedAmount || b.amount
        return <IndikatorSerapan persen={dasar ? (b.realizedAmount / dasar) * 100 : NaN} />
      },
    },
  ]

  return (
    <Modal
      terbuka={anggaran !== null}
      onTutup={onTutup}
      judul={anggaran ? `Baris Anggaran — ${anggaran.name}` : 'Baris Anggaran'}
      deskripsi="Kolom anggaran, realisasi, sisa, dan persentase serapan dengan indikator peringatan 80/90/100%."
      lebar="max-w-5xl"
      footer={
        <Button varian="garis" onClick={onTutup}>
          Tutup
        </Button>
      }
    >
      {galat ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{galat}</p>
      ) : null}
      <DataTable
        kolom={kolom}
        data={baris}
        kunciBaris={(b) => b.id}
        memuat={memuat}
        galat={galat}
        kodeGalat={kodeGalat}
        onCoba={muat}
        pesanKosong="Belum ada baris anggaran untuk anggaran ini."
      />
    </Modal>
  )
}

function FormRevisi({
  anggaran,
  onTutup,
  setelah,
}: {
  anggaran: Anggaran | null
  onTutup: () => void
  setelah: () => void
}) {
  const [alasan, setAlasan] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const kirim = async () => {
    setGalat(null)
    if (!anggaran) return
    if (alasan.trim().length < 10) return setGalat('Alasan revisi minimal 10 karakter.')
    setMemuat(true)
    try {
      await api.post(
        '/budget-revisions',
        { budgetId: anggaran.id, reason: alasan.trim() },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setAlasan('')
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={anggaran !== null}
      onTutup={onTutup}
      judul="Ajukan Revisi Anggaran"
      deskripsi="Revisi harus disetujui sebelum nominal anggaran berubah."
      lebar="max-w-xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void kirim()} memuat={memuat}>
            Ajukan Revisi
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <Card padat className="p-3">
          <p className="text-sm text-netral-700">
            Anggaran: <strong>{anggaran?.name}</strong> ({anggaran?.budgetCode})
          </p>
          <p className="mt-1 text-sm text-netral-500">
            Anggaran final saat ini: {formatRupiah(anggaran?.totalFinal ?? 0)}
          </p>
        </Card>
        <Textarea
          label="Alasan revisi"
          value={alasan}
          onChange={(e) => setAlasan(e.target.value)}
          rows={3}
          bantuan="Minimal 10 karakter. Rincian per baris dapat ditambahkan setelah revisi dibuat."
        />
        <Input
          label="Catatan tambahan"
          placeholder="Opsional"
          value=""
          readOnly
          bantuan="Ubah nominal per baris melalui endpoint baris revisi setelah revisi dibuat."
        />
      </div>
    </Modal>
  )
}
